import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit2, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Fornecedores() {
  const [fornecedores, setFornecedores] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [novoTipoDialogOpen, setNovoTipoDialogOpen] = useState(false);
  const [novoTipoNome, setNovoTipoNome] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [currentFornecedor, setCurrentFornecedor] = useState(null);
  const [filtroGlobal, setFiltroGlobal] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fornecedorParaExcluir, setFornecedorParaExcluir] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    contato_principal: '',
    telefone: '',
    email: '',
    cidade: '',
    tipo_servico: '',
  });

  useEffect(() => {
    carregarFornecedores();
  }, []);

  const carregarFornecedores = async () => {
    try {
      const [fornRes, tiposRes] = await Promise.all([
        axios.get(`${API}/fornecedores`),
        axios.get(`${API}/tipos-servico`),
      ]);
      setFornecedores(fornRes.data);
      setTiposServico(tiposRes.data);
    } catch (error) {
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await axios.put(`${API}/fornecedores/${currentFornecedor.id}`, formData);
        toast.success('Fornecedor atualizado com sucesso!');
      } else {
        await axios.post(`${API}/fornecedores`, formData);
        toast.success('Fornecedor criado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      carregarFornecedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar fornecedor');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      contato_principal: '',
      telefone: '',
      email: '',
      cidade: '',
      tipo_servico: '',
    });
    setEditMode(false);
    setCurrentFornecedor(null);
  };

  const handleEdit = (fornecedor) => {
    setCurrentFornecedor(fornecedor);
    setFormData({
      nome: fornecedor.nome,
      contato_principal: fornecedor.contato_principal,
      telefone: fornecedor.telefone,
      email: fornecedor.email || '',
      cidade: fornecedor.cidade || '',
      tipo_servico: fornecedor.tipo_servico,
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDeleteClick = (fornecedor) => {
    setFornecedorParaExcluir(fornecedor);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fornecedorParaExcluir) return;
    try {
      await axios.delete(`${API}/fornecedores/${fornecedorParaExcluir.id}`);
      toast.success('Fornecedor excluído com sucesso!');
      carregarFornecedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir fornecedor');
    } finally {
      setDeleteDialogOpen(false);
      setFornecedorParaExcluir(null);
    }
  };

  const handleAdicionarTipoServico = async () => {
    if (!novoTipoNome.trim()) {
      toast.error('Digite o nome do tipo de serviço');
      return;
    }

    try {
      await axios.post(`${API}/tipos-servico`, { nome: novoTipoNome });
      toast.success('Tipo de serviço adicionado com sucesso!');
      setNovoTipoNome('');
      setNovoTipoDialogOpen(false);
      
      // Recarregar tipos
      const tiposRes = await axios.get(`${API}/tipos-servico`);
      setTiposServico(tiposRes.data);
      
      // Selecionar o novo tipo
      setFormData({ ...formData, tipo_servico: novoTipoNome });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar tipo de serviço');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="fornecedores-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-1 lg:mb-2">Fornecedores</h1>
          <p className="text-sm sm:text-base text-slate-600">Gerencie o cadastro de fornecedores</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="criar-fornecedor-btn">
              <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" /> Novo Fornecedor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Editar Fornecedor' : 'Novo Fornecedor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome da Empresa *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome do fornecedor"
                  required
                  data-testid="input-nome"
                />
              </div>

              <div>
                <Label>Contato Principal *</Label>
                <Input
                  value={formData.contato_principal}
                  onChange={(e) => setFormData({ ...formData, contato_principal: e.target.value })}
                  placeholder="Nome do responsável"
                  required
                  data-testid="input-contato"
                />
              </div>

              <div>
                <Label>Telefone *</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  required
                  data-testid="input-telefone"
                />
              </div>

              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@fornecedor.com"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label>Cidade *</Label>
                <Input
                  value={formData.cidade}
                  onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                  placeholder="Cidade do fornecedor"
                  required
                  data-testid="input-cidade"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Tipo de Serviço *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setNovoTipoDialogOpen(true)}
                    className="h-6 text-xs text-blue-600"
                    data-testid="btn-adicionar-tipo-servico"
                  >
                    + Adicionar Novo
                  </Button>
                </div>
                <Select
                  value={formData.tipo_servico}
                  onValueChange={(value) => setFormData({ ...formData, tipo_servico: value })}
                  required
                >
                  <SelectTrigger data-testid="select-tipo-servico">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposServico.map((tipo) => (
                      <SelectItem key={tipo.id} value={tipo.nome}>
                        {tipo.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="salvar-fornecedor-btn">
                  {editMode ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campo de busca */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar fornecedores..."
            value={filtroGlobal}
            onChange={(e) => setFiltroGlobal(e.target.value)}
            className="pl-10 text-sm"
          />
        </div>
      </div>

      {/* Layout Mobile - Cards */}
      <div className="lg:hidden space-y-4">
        {fornecedores.filter(f => 
          f.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
          f.contato_principal?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
          f.telefone?.includes(filtroGlobal) ||
          f.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
        ).length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center text-slate-500">
            {filtroGlobal ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
          </div>
        ) : (
          fornecedores.filter(f => 
            f.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
            f.contato_principal?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
            f.telefone?.includes(filtroGlobal) ||
            f.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
          ).map((fornecedor) => (
            <div key={fornecedor.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800 text-sm">{fornecedor.nome}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{fornecedor.contato_principal}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Telefone:</span>
                  <span className="font-medium text-slate-800">{fornecedor.telefone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Cidade:</span>
                  <span className="font-medium text-slate-800">{fornecedor.cidade || '-'}</span>
                </div>
                {fornecedor.email && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">E-mail:</span>
                    <span className="font-medium text-slate-800 text-xs">{fornecedor.email}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-600">Serviço:</span>
                  <span className="font-medium text-slate-800">{fornecedor.tipo_servico}</span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-200 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(fornecedor)}
                  className="flex-1"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteClick(fornecedor)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Layout Desktop - Tabela */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs xl:text-sm">Nome</TableHead>
              <TableHead className="text-xs xl:text-sm">Contato</TableHead>
              <TableHead className="text-xs xl:text-sm">Telefone</TableHead>
              <TableHead className="text-xs xl:text-sm hidden xl:table-cell">Cidade</TableHead>
              <TableHead className="text-xs xl:text-sm hidden 2xl:table-cell">E-mail</TableHead>
              <TableHead className="text-xs xl:text-sm">Tipo de Serviço</TableHead>
              <TableHead className="text-xs xl:text-sm w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fornecedores.filter(f => 
              f.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
              f.contato_principal?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
              f.telefone?.includes(filtroGlobal) ||
              f.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
            ).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  {filtroGlobal ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
                </TableCell>
              </TableRow>
            ) : (
              fornecedores.filter(f => 
                f.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
                f.contato_principal?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
                f.telefone?.includes(filtroGlobal) ||
                f.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
              ).map((fornecedor) => (
                <TableRow key={fornecedor.id} data-testid={`fornecedor-row-${fornecedor.id}`}>
                  <TableCell className="font-medium text-xs xl:text-sm">{fornecedor.nome}</TableCell>
                  <TableCell className="text-xs xl:text-sm">{fornecedor.contato_principal}</TableCell>
                  <TableCell className="text-xs xl:text-sm">{fornecedor.telefone}</TableCell>
                  <TableCell className="text-xs xl:text-sm hidden xl:table-cell">{fornecedor.cidade || '-'}</TableCell>
                  <TableCell className="text-xs xl:text-sm hidden 2xl:table-cell">{fornecedor.email || '-'}</TableCell>
                  <TableCell className="text-xs xl:text-sm">{fornecedor.tipo_servico}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(fornecedor)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-fornecedor-${fornecedor.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(fornecedor)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-fornecedor-${fornecedor.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog para adicionar novo tipo de serviço */}
      <Dialog open={novoTipoDialogOpen} onOpenChange={setNovoTipoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Tipo de Serviço</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Tipo *</Label>
              <Input
                value={novoTipoNome}
                onChange={(e) => setNovoTipoNome(e.target.value)}
                placeholder="Ex: Locação de Veículos, Spa, Passeios"
                data-testid="input-novo-tipo-servico"
                onKeyPress={(e) => e.key === 'Enter' && handleAdicionarTipoServico()}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setNovoTipoDialogOpen(false);
                  setNovoTipoNome('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAdicionarTipoServico}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="btn-salvar-tipo-servico"
              >
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Tem certeza que deseja excluir <strong>{fornecedorParaExcluir?.nome}</strong>?</p>
            <p className="text-sm text-red-600 mt-2">Esta ação não pode ser desfeita.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}